import { useEffect, useMemo, useRef, useState } from 'react'
import { useDashReport } from '../hooks/useDashReport'
import { HorizontalBarChart } from '../components'

interface ItemStat {
  nombre: string
  total: number
  con_telefono: number
  con_email: number
  con_email_telefono: number
  porcentaje: number
}

interface CruceCelda {
  ciudad: string
  especialidad: string
  total: number
  con_telefono: number
}

interface BaseMedicosUnificadaReport {
  titulo: string
  subtitulo: string
  total: number
  con_email: number
  con_telefono: number
  con_email_telefono: number
  pct_email: number
  pct_telefono: number
  pct_email_telefono: number
  ciudades: ItemStat[]
  especialidades: ItemStat[]
  cruce?: CruceCelda[]
}

const TOP_GRAFICA = 15

/** Multiselect con búsqueda para listas largas (ciudades / especialidades). */
function MultiSelect({
  label,
  placeholder,
  options,
  selected,
  onChange,
  color,
}: {
  label: string
  placeholder: string
  options: string[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
  color: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const visibles = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => o.toLowerCase().includes(q))
  }, [options, query])

  function toggle(opt: string) {
    const next = new Set(selected)
    if (next.has(opt)) next.delete(opt)
    else next.add(opt)
    onChange(next)
  }

  const resumen =
    selected.size === 0
      ? placeholder
      : selected.size === 1
        ? [...selected][0]
        : `${selected.size} seleccionadas`

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-left hover:border-gray-400 focus:outline-none focus:ring-2 transition-colors"
        style={{ ['--tw-ring-color' as string]: color }}
      >
        <span className={`truncate ${selected.size === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
          {resumen}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transform transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Buscar…"
              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1"
              style={{ ['--tw-ring-color' as string]: color }}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {visibles.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400">Sin resultados</p>
            ) : (
              visibles.map(opt => {
                const checked = selected.has(opt)
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggle(opt)}
                    className="w-full flex items-center px-3 py-1.5 text-sm text-left hover:bg-gray-50"
                  >
                    <span
                      className="w-4 h-4 mr-2 flex-shrink-0 rounded border flex items-center justify-center"
                      style={{
                        borderColor: checked ? color : '#d1d5db',
                        backgroundColor: checked ? color : 'transparent',
                      }}
                    >
                      {checked && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className="truncate text-gray-700">{opt}</span>
                  </button>
                )
              })
            )}
          </div>
          {selected.size > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => onChange(new Set())}
                className="text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                Limpiar selección
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function BaseMedicosUnificadaPage() {
  const { data, isLoading, error } = useDashReport<BaseMedicosUnificadaReport>('bd/colombia/validacion')
  const [ciudadesOpen, setCiudadesOpen] = useState(false)
  const [espOpen, setEspOpen] = useState(false)
  const [filtroCiudades, setFiltroCiudades] = useState<Set<string>>(new Set())
  const [filtroEsp, setFiltroEsp] = useState<Set<string>>(new Set())

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-5xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#57a6d8]" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-5xl mx-auto flex items-center justify-center min-h-[400px]">
          <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
        </div>
      </div>
    )
  }

  function gradientColors(items: ItemStat[], hex: string, minAlpha = 0.25): string[] {
    const max = Math.max(...items.map(i => i.total), 1)
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return items.map(item => {
      const alpha = minAlpha + (1 - minAlpha) * (item.total / max)
      return `rgba(${r},${g},${b},${alpha.toFixed(2)})`
    })
  }

  // Opciones de filtro = universo completo (ordenado por volumen desc).
  const opcionesCiudades = data.ciudades.map(c => c.nombre)
  const opcionesEsp = data.especialidades.map(e => e.nombre)

  const cruce = data.cruce ?? []
  const hayCruce = cruce.length > 0
  const hayFiltros = filtroCiudades.size > 0 || filtroEsp.size > 0

  // Cruce filtrado según selección. Cada médico cae en una sola celda,
  // por lo que sumar celdas da totales exactos.
  const cruceFiltrado = cruce.filter(
    c =>
      (filtroCiudades.size === 0 || filtroCiudades.has(c.ciudad)) &&
      (filtroEsp.size === 0 || filtroEsp.has(c.especialidad)),
  )

  function agrupar(celdas: CruceCelda[], key: 'ciudad' | 'especialidad', total: number): ItemStat[] {
    const map = new Map<string, { total: number; con_telefono: number }>()
    for (const c of celdas) {
      const k = c[key]
      const cur = map.get(k) ?? { total: 0, con_telefono: 0 }
      cur.total += c.total
      cur.con_telefono += c.con_telefono
      map.set(k, cur)
    }
    return [...map.entries()]
      .map(([nombre, v]) => ({
        nombre,
        total: v.total,
        con_telefono: v.con_telefono,
        con_email: v.total, // todos los médicos del universo tienen email
        con_email_telefono: v.con_telefono,
        porcentaje: total ? Math.round((v.total / total) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.total - a.total)
  }

  // Totales y desgloses: derivados del cruce si existe; si no, del agregado original.
  const totalG = hayCruce ? cruceFiltrado.reduce((s, c) => s + c.total, 0) : data.total
  const telefonoG = hayCruce ? cruceFiltrado.reduce((s, c) => s + c.con_telefono, 0) : data.con_telefono
  const emailG = hayCruce ? totalG : data.con_email // todos tienen email
  const emailTelefonoG = telefonoG

  const pct = (parte: number) => (totalG ? Math.round((parte / totalG) * 1000) / 10 : 0)

  const ciudadesStats = hayCruce ? agrupar(cruceFiltrado, 'ciudad', totalG) : data.ciudades
  const espStats = hayCruce ? agrupar(cruceFiltrado, 'especialidad', totalG) : data.especialidades

  const ciudadesTop = ciudadesStats.slice(0, TOP_GRAFICA)
  const espTop = espStats.slice(0, TOP_GRAFICA)

  const ciudadColors = gradientColors(ciudadesTop, '#57a6d8')
  const espColors = gradientColors(espTop, '#8d82bc')

  const chartCiudades = ciudadesTop.map((c, i) => ({ label: c.nombre, value: c.total, color: ciudadColors[i] }))
  const chartEsp = espTop.map((e, i) => ({ label: e.nombre, value: e.total, color: espColors[i] }))

  const kpis = [
    { label: 'Total Médicos', valor: totalG, pct: null as number | null, color: '#2b2b2b' },
    { label: 'Con Email', valor: emailG, pct: pct(emailG), color: '#2fbc80' },
    { label: 'Con Teléfono', valor: telefonoG, pct: pct(telefonoG), color: '#57a6d8' },
    { label: 'Email + Teléfono', valor: emailTelefonoG, pct: pct(emailTelefonoG), color: '#8d82bc' },
  ]

  function CoverageTable({ items }: { items: ItemStat[] }) {
    return (
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full bg-white text-xs lg:text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 lg:px-4 lg:py-3 text-left font-semibold text-gray-700">Nombre</th>
              <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Total</th>
              <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Con teléfono</th>
              <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Con email</th>
              <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Email + teléfono</th>
              <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">% del total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map(item => (
              <tr key={item.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{item.nombre}</td>
                <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{item.total.toLocaleString('es-CO')}</td>
                <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-[#57a6d8]">{item.con_telefono.toLocaleString('es-CO')}</td>
                <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-[#2fbc80]">{item.con_email.toLocaleString('es-CO')}</td>
                <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-[#8d82bc]">{item.con_email_telefono.toLocaleString('es-CO')}</td>
                <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">{item.porcentaje}%</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-300">
            <tr>
              <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
              <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{totalG.toLocaleString('es-CO')}</td>
              <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-[#57a6d8]">{telefonoG.toLocaleString('es-CO')}</td>
              <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-[#2fbc80]">{emailG.toLocaleString('es-CO')}</td>
              <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-[#8d82bc]">{emailTelefonoG.toLocaleString('es-CO')}</td>
              <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{totalG ? 100 : 0}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  function CollapsibleTable({
    titulo,
    cantidad,
    open,
    onToggle,
    items,
  }: {
    titulo: string
    cantidad: number
    open: boolean
    onToggle: () => void
    items: ItemStat[]
  }) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
        <button
          onClick={onToggle}
          className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
        >
          <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-[#57a6d8] transition-colors duration-200">
            <div className="flex items-center flex-wrap">
              <svg className="w-5 h-5 text-[#57a6d8] mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span className="text-base lg:text-lg">{titulo}</span>
              <span className="ml-2 text-xs lg:text-sm text-gray-500">({cantidad.toLocaleString('es-CO')})</span>
            </div>
            <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${open ? 'rotate-180' : ''}`}
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        {open && <CoverageTable items={items} />}
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex items-center">
            <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
              <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        {hayCruce && (
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 flex items-center text-base lg:text-lg">
                <svg className="w-5 h-5 text-[#57a6d8] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                Filtros
              </h2>
              {hayFiltros && (
                <button
                  type="button"
                  onClick={() => { setFiltroCiudades(new Set()); setFiltroEsp(new Set()) }}
                  className="text-xs lg:text-sm font-medium text-[#eb6587] hover:underline"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultiSelect
                label="Ciudad"
                placeholder="Todas las ciudades"
                options={opcionesCiudades}
                selected={filtroCiudades}
                onChange={setFiltroCiudades}
                color="#57a6d8"
              />
              <MultiSelect
                label="Especialidad"
                placeholder="Todas las especialidades"
                options={opcionesEsp}
                selected={filtroEsp}
                onChange={setFiltroEsp}
                color="#8d82bc"
              />
            </div>
            {hayFiltros && (
              <p className="mt-3 text-xs lg:text-sm text-gray-500">
                Mostrando <span className="font-semibold text-gray-700">{totalG.toLocaleString('es-CO')}</span> de{' '}
                {data.total.toLocaleString('es-CO')} médicos según los filtros aplicados.
              </p>
            )}
          </div>
        )}

        {/* Resumen General */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-[#57a6d8] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map(kpi => (
              <div key={kpi.label} className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-3xl font-bold" style={{ color: kpi.color }}>
                  {kpi.valor.toLocaleString('es-CO')}
                </p>
                <p className="text-sm text-gray-600 mt-1">{kpi.label}</p>
                {kpi.pct !== null && (
                  <p className="text-xs font-semibold mt-0.5" style={{ color: kpi.color }}>{kpi.pct}%</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Gráficas */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-[#57a6d8] mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Médicos por Ciudad (top {TOP_GRAFICA})
            </h3>
            {chartCiudades.length > 0 ? (
              <HorizontalBarChart data={chartCiudades} />
            ) : (
              <p className="text-sm text-gray-400 py-8 text-center">Sin datos para los filtros aplicados.</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-[#8d82bc] mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Médicos por Especialidad (top {TOP_GRAFICA})
            </h3>
            {chartEsp.length > 0 ? (
              <HorizontalBarChart data={chartEsp} />
            ) : (
              <p className="text-sm text-gray-400 py-8 text-center">Sin datos para los filtros aplicados.</p>
            )}
          </div>
        </div>

        {/* Desgloses con cobertura de contacto */}
        <CollapsibleTable
          titulo="Desglose por Ciudad"
          cantidad={ciudadesStats.length}
          open={ciudadesOpen}
          onToggle={() => setCiudadesOpen(o => !o)}
          items={ciudadesStats}
        />
        <CollapsibleTable
          titulo="Desglose por Especialidad"
          cantidad={espStats.length}
          open={espOpen}
          onToggle={() => setEspOpen(o => !o)}
          items={espStats}
        />

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies — Base de Médicos Unificada</p>
        </div>

      </div>
    </div>
  )
}
